import { useRef, useState, useEffect, type MutableRefObject } from 'react';

const LIMIT_UNIQUE_RESIZE_OBSERVATIONS = 15;

// Observes and yields unique heights for element targeted by attaching
// the returned `containerRef`.
export const useHeightObserver = (): {
	containerRef: MutableRefObject<HTMLDivElement | null>;
	height: number | null;
	observationInvalidatedReason: string | null;
} => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState<number | null>(null);
	const [observationInvalidatedReason, setObservationInvalidatedReason] = useState<string | null>(
		null,
	);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		let uniqueSizeObservations = 0;

		// Also handles initial height in case ref->element attachment is delayed.
		const observer = new ResizeObserver(([entry]) => {
			setHeight((prevHeight) => {
				const height = Math.trunc(entry.contentRect.height);
				if (prevHeight !== height) {
					uniqueSizeObservations++;
				}
				return height;
			});

			// Break after too many unique changes.
			if (uniqueSizeObservations >= LIMIT_UNIQUE_RESIZE_OBSERVATIONS) {
				observer.disconnect();
				setObservationInvalidatedReason(
					`Stopped counting after ${LIMIT_UNIQUE_RESIZE_OBSERVATIONS} unique resize events`,
				);
			}
		});

		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	return { containerRef, height, observationInvalidatedReason };
};
